<template>
  <van-overlay :show="showModel" :z-index="zIndex">
    <div
      class="viewer-wrapper"
      ref="wrapperRef"
      @click.stop
      @wheel.prevent="onWheel"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @touchstart.passive="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onTouchEnd"
      @mousedown="onMouseDown"
    >
      <div
        class="pdf-canvas"
        ref="canvasContainer"
        :style="{
          transform: 'translate(' + translateX + 'px,' + translateY + 'px) scale(' + scale + ')',
          transition: transitionStyle,
        }"
      ></div>
      <div class="viewer-toolbar">
        <van-icon name="cross" color="#000" size="25" class="viewer-close" @click="close" />
      </div>
      <div class="wm-overlay-in-viewer" :style="wmStyle"></div>
      <div v-if="loading" class="loading"><van-loading type="spinner" /></div>
    </div>
  </van-overlay>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'

const showModel = defineModel<boolean>('show')

const props = defineProps<{
  src: string
  zIndex?: number
  rubberFactor?: number
  minScale?: number
  maxScale?: number
  watermarkText?: string
}>()

const zIndex = computed(() => props.zIndex ?? 2000)
const rubberFactor = computed(() => props.rubberFactor ?? 0.6)
const minScale = computed(() => props.minScale ?? 0.5)
const maxScale = computed(() => props.maxScale ?? 4)

const wrapperRef = ref<HTMLElement | null>(null)
const canvasContainer = ref<HTMLElement | null>(null)
const loading = ref(false)
let pdfDoc: any = null
let pdfWorker: Worker | null = null

const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const transitioning = ref(false)
const transitionStyle = computed(() => (transitioning.value ? 'transform 0.2s ease' : ''))
const wmStyle = computed(() => {
  const text = String(props.watermarkText || '')
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='260'><text x='30%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='rgba(0,0,0,0.12)' font-size='12' font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' transform='rotate(-30,130,130)'>${text}</text></svg>`
  const url = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
  return { backgroundImage: `url("${url}")` }
})

const dragging = ref(false)
const lastX = ref(0)
const lastY = ref(0)
const pinchStartDistance = ref(0)
const pinchStartScale = ref(1)

const ensurePdfWorker = () => {
  if (typeof window === 'undefined') return
  if (!pdfWorker) {
    pdfWorker = new Worker(new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url), {
      type: 'module',
    })
    ;(pdfjsLib as any).GlobalWorkerOptions.workerPort = pdfWorker
    pdfWorker.addEventListener('error', () => {
      try {
        pdfWorker?.terminate()
      } catch {}
      pdfWorker = null
      ;(pdfjsLib as any).GlobalWorkerOptions.workerPort = null
    })
  }
}

const renderPdf = async (url: string) => {
  if (!canvasContainer.value) return
  canvasContainer.value.innerHTML = ''
  ensurePdfWorker()
  loading.value = true
  try {
    const task = pdfjsLib.getDocument({ url })
    const pdf = await task.promise
    pdfDoc = pdf
    const rect = wrapperRef.value?.getBoundingClientRect()
    const maxWidth = (rect?.width || window.innerWidth) - 40
    const n = pdf.numPages
    const fragment = document.createDocumentFragment()
    for (let i = 1; i <= n; i++) {
      const page = await pdf.getPage(i)
      const base = page.getViewport({ scale: 1 })
      const s = Math.min(2, Math.max(0.1, maxWidth / base.width))
      const v = page.getViewport({ scale: s })
      const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))
      const canvas = document.createElement('canvas')
      canvas.style.width = Math.floor(v.width) + 'px'
      canvas.style.height = Math.floor(v.height) + 'px'
      canvas.width = Math.floor(v.width * dpr)
      canvas.height = Math.floor(v.height * dpr)
      const ctx = canvas.getContext('2d')!
      const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined
      await page.render({ canvasContext: ctx, viewport: v, transform }).promise
      fragment.appendChild(canvas)
    }
    canvasContainer.value.appendChild(fragment)
  } catch {}
  loading.value = false
}

const getTouchDistance = (e: TouchEvent) => {
  if ((e.touches && e.touches.length >= 2) || (e.changedTouches && e.changedTouches.length >= 2)) {
    const t1 = e.touches[0] || e.changedTouches[0]
    const t2 = e.touches[1] || e.changedTouches[1]
    if (t1 && t2) {
      const dx = t1.clientX - t2.clientX
      const dy = t1.clientY - t2.clientY
      return Math.sqrt(dx * dx + dy * dy)
    }
  }
  return 0
}
const applyRubber = (val: number, limit: number) => {
  return Math.max(-limit, Math.min(limit, val))
}
const getLimits = () => {
  const wrap = wrapperRef.value
  const cont = canvasContainer.value
  if (!wrap || !cont) return { limitX: 0, limitY: 0 }
  const w = wrap.clientWidth
  const h = wrap.clientHeight
  const bw = cont.scrollWidth || cont.clientWidth
  const bh = cont.scrollHeight || cont.clientHeight
  const sw = bw * scale.value
  const sh = bh * scale.value
  const limitX = sw > w ? (sw - w) / 2 : 0
  const limitY = sh > h ? (sh - h) / 2 : 0
  return { limitX, limitY }
}
const zoomAt = (anchorX: number, anchorY: number, next: number) => {
  const rect = wrapperRef.value?.getBoundingClientRect()
  if (!rect) {
    scale.value = next
    return
  }
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const r = next / scale.value
  const vx = anchorX - cx
  const vy = anchorY - cy
  translateX.value += vx * (1 - r)
  translateY.value += vy * (1 - r)
  scale.value = next
  const { limitX, limitY } = getLimits()
  translateX.value = applyRubber(translateX.value, limitX)
  translateY.value = applyRubber(translateY.value, limitY)
}

const onTouchStart = (e: TouchEvent) => {
  if ((e.touches && e.touches.length >= 2) || (e.changedTouches && e.changedTouches.length >= 2)) {
    pinchStartDistance.value = getTouchDistance(e)
    pinchStartScale.value = scale.value
    dragging.value = false
    return
  }
  const t = e.touches[0] || e.changedTouches[0]
  if (!t) return
  dragging.value = true
  lastX.value = t.clientX
  lastY.value = t.clientY
}
const onTouchMove = (e: TouchEvent) => {
  if ((e.touches && e.touches.length >= 2) || (e.changedTouches && e.changedTouches.length >= 2)) {
    const d = getTouchDistance(e)
    if (d > 0 && pinchStartDistance.value > 0) {
      const next = Math.max(
        minScale.value,
        Math.min(maxScale.value, pinchStartScale.value * (d / pinchStartDistance.value)),
      )
      const t1 = e.touches[0] || e.changedTouches[0]
      const t2 = e.touches[1] || e.changedTouches[1]
      if (t1 && t2) {
        const ax = (t1.clientX + t2.clientX) / 2
        const ay = (t1.clientY + t2.clientY) / 2
        zoomAt(ax, ay, next)
      } else {
        scale.value = next
      }
    }
    return
  }
  if (!dragging.value) return
  const t = e.touches[0] || e.changedTouches[0]
  if (!t) return
  const dx = t.clientX - lastX.value
  const dy = t.clientY - lastY.value
  const { limitX, limitY } = getLimits()
  const nextX = translateX.value + dx
  const nextY = translateY.value + dy
  translateX.value = applyRubber(nextX, limitX)
  translateY.value = applyRubber(nextY, limitY)
  lastX.value = t.clientX
  lastY.value = t.clientY
}
const onTouchEnd = (e: TouchEvent) => {
  if (e.touches && e.touches.length < 2 && e.changedTouches && e.changedTouches.length < 2) {
    pinchStartDistance.value = 0
  }
  if (!e.touches || e.touches.length === 0) {
    dragging.value = false
    const { limitX, limitY } = getLimits()
    const targetX = Math.max(-limitX, Math.min(limitX, translateX.value))
    const targetY = Math.max(-limitY, Math.min(limitY, translateY.value))
    if (targetX !== translateX.value || targetY !== translateY.value) {
      transitioning.value = true
      translateX.value = targetX
      translateY.value = targetY
      window.setTimeout(() => {
        transitioning.value = false
      }, 200)
    }
  }
}
const onMouseDown = (e: MouseEvent) => {
  dragging.value = true
  lastX.value = e.clientX
  lastY.value = e.clientY
}
const onMouseMove = (e: MouseEvent) => {
  if (!dragging.value) return
  const dx = e.clientX - lastX.value
  const dy = e.clientY - lastY.value
  const { limitX, limitY } = getLimits()
  const nextX = translateX.value + dx
  const nextY = translateY.value + dy
  translateX.value = applyRubber(nextX, limitX)
  translateY.value = applyRubber(nextY, limitY)
  lastX.value = e.clientX
  lastY.value = e.clientY
}
const onMouseUp = () => {
  dragging.value = false
}
const onWheel = (e: WheelEvent) => {
  e.preventDefault()
  const step = e.deltaY > 0 ? -0.1 : 0.1
  const next = Math.max(minScale.value, Math.min(maxScale.value, scale.value + step))
  const rect = wrapperRef.value?.getBoundingClientRect()
  if (rect) {
    const ax = e.clientX
    const ay = e.clientY
    zoomAt(ax, ay, next)
  } else {
    scale.value = next
  }
}
const close = () => {
  showModel.value = false
}

watch(showModel, async (val) => {
  if (val) {
    scale.value = 1
    translateX.value = 0
    translateY.value = 0
    await nextTick()
    await renderPdf(props.src)
    await nextTick()
    const { limitY } = getLimits()
    translateX.value = 0
    translateY.value = limitY
  } else {
    try {
      if (pdfDoc && pdfDoc.destroy) await pdfDoc.destroy()
    } catch {}
    pdfDoc = null
  }
})
</script>

<style scoped lang="scss">
.viewer-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
  background: #fff;
}
.pdf-canvas {
  user-select: none;
  cursor: grab;
  transform-origin: center center;
  min-width: 0;
  min-height: 0;
}
.pdf-canvas:active {
  cursor: grabbing;
}
.viewer-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.viewer-close {
  cursor: pointer;
  font-size: 25px;
}
.loading {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wm-overlay-in-viewer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  background-repeat: repeat;
  background-size: 260px 260px;
}
</style>
